import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import type {
  SectionKey,
  SectionMode,
} from "../../../../../types/clientConfig";
import {
  getActiveClientNoWidgetMessageKey,
  getCoachNoDataMessageKey,
  getEffectiveSectionMode,
  getWidgetPageHref,
  getWidgetRequiredMessageKey,
  isClientOptionBlockedByMissingWidget,
  isSectionDisabledByMissingWidget,
  SECTION_RADIO_CONFIG,
  type WidgetDependentSectionKey,
} from "./utils";

type SectionBlock = {
  error: string;
  messageKey: string;
  deleteUrl?: string;
};

interface SectionRadioGroupsProps {
  roleLabels: Record<string, string>;
  sectionWidgetAvailability: Record<WidgetDependentSectionKey, boolean>;
  getSectionMode: (key: SectionKey) => SectionMode | undefined;
  onSectionModeChange?: (key: SectionKey, mode: SectionMode) => void;
  idPrefix?: string;
  isClientOverride?: (key: SectionKey) => boolean;
  getBlock?: (key: SectionKey) => SectionBlock | null;
  variant?: "edit" | "readonly";
  showSectionIcons?: boolean;
}

export const SectionRadioGroups = ({
  roleLabels,
  sectionWidgetAvailability,
  getSectionMode,
  onSectionModeChange,
  idPrefix = "",
  isClientOverride = () => false,
  getBlock,
  variant = "edit",
  showSectionIcons: _showSectionIcons = true,
}: SectionRadioGroupsProps) => {
  const { t } = useTranslation("athlete-portal");

  return (
    <>
      {SECTION_RADIO_CONFIG.map((block) => {
        const effectiveMode =
          variant === "readonly"
            ? getSectionMode(block.key) ?? "hidden"
            : getEffectiveSectionMode(getSectionMode(block.key), {
                isClientOverride: isClientOverride(block.key),
              });

        const widgetRequiredMessageKey = getWidgetRequiredMessageKey(block.key);
        const widgetPageHref = getWidgetPageHref(block.key);
        const inputIdPrefix = idPrefix ? `${idPrefix}-` : "";
        // Only show the block when the section is not already hidden — the block
        // applies to *turning off*, so it's irrelevant when it's already off.
        const sectionBlock =
          effectiveMode !== "hidden" ? getBlock?.(block.key) ?? null : null;

        // Attention banner: section is actively set to client mode but the
        // required widget has since been removed.
        const activeClientNoWidgetMessageKey =
          effectiveMode === "client" &&
          isSectionDisabledByMissingWidget(block.key, sectionWidgetAvailability)
            ? getActiveClientNoWidgetMessageKey(block.key)
            : null;

        // Attention banner: section is in coach mode but no data has been
        // assigned to the client yet — coach needs to take action.
        const coachNoDataMessageKey =
          variant === "edit" &&
          effectiveMode === "coach" &&
          getBlock !== undefined &&
          getBlock(block.key) === null
            ? getCoachNoDataMessageKey(block.key)
            : null;

        return (
          <div
            key={block.key}
            id={`section-${block.key}`}
            className="client-config-card"
          >
            <div className="client-config-card__header">
              <div className="client-config-card__title">
                <span>
                  {block.emoji} {t(block.titleKey)}
                </span>
              </div>
              {effectiveMode === "hidden" && (
                <span className="chip info">
                  {t("clientConfiguration.chips.hidden")}
                </span>
              )}
              {isClientOverride(block.key) && (
                <span className="chip warning">
                  {t("clientConfiguration.chips.clientOverride", roleLabels)}
                </span>
              )}
            </div>
            {activeClientNoWidgetMessageKey && (
              <div className="client-config-card__attention chip warning">
                <div>
                  <span>{t(activeClientNoWidgetMessageKey)}</span>
                  {widgetPageHref && (
                    <a href={widgetPageHref}>
                      <span>
                        {t("clientConfiguration.widgetRequired.goToWidgets")}
                      </span>
                    </a>
                  )}
                </div>
              </div>
            )}
            {coachNoDataMessageKey && (
              <div className="client-config-card__attention chip warning">
                <div>
                  <span>{t(coachNoDataMessageKey)}</span>
                </div>
              </div>
            )}
            <div className="client-config-radio-list">
              {block.options.map((opt) => {
                if (variant === "readonly" && effectiveMode !== opt.value) {
                  return null;
                }

                const isWidgetBlocked = isClientOptionBlockedByMissingWidget(
                  block.key,
                  opt.value,
                  sectionWidgetAvailability
                );
                const isDataBlocked =
                  opt.value === "hidden" && Boolean(sectionBlock);
                const isBlocked = isWidgetBlocked || isDataBlocked;

                const radioRow = (
                  <div className="client-config-radio">
                    {variant === "readonly" && (
                      <SvgIcon src="/img/new-icons/vuesax-linear-tick-circle-gradient.svg" />
                    )}
                    {variant === "edit" && (
                      <input
                        name={`${inputIdPrefix}${block.key}`}
                        id={`${inputIdPrefix}${block.key}-${opt.value}`}
                        type="radio"
                        value={`${block.key}-${opt.value}`}
                        className="client-config-radio__input"
                        onChange={() =>
                          onSectionModeChange?.(block.key, opt.value)
                        }
                        checked={effectiveMode === opt.value}
                        disabled={isBlocked}
                      />
                    )}
                    <label
                      className="radio-label"
                      htmlFor={
                        variant === "edit"
                          ? `${inputIdPrefix}${block.key}-${opt.value}`
                          : undefined
                      }
                    >
                      <span className="client-config-radio__label p4">
                        {t(opt.labelKey, roleLabels)}
                      </span>
                      <span className="client-config-radio__desc p6">
                        {t(opt.descKey, roleLabels)}
                      </span>
                    </label>
                  </div>
                );

                if (isBlocked && variant === "edit") {
                  const chipContent =
                    isWidgetBlocked && widgetRequiredMessageKey
                      ? t(widgetRequiredMessageKey)
                      : sectionBlock
                      ? t(`clientConfiguration.sectionBlocks.${block.key}`)
                      : "";
                  const chipClass =
                    "chip warning client-config-radio__widget-overlay-chip";

                  const linkHref = isWidgetBlocked
                    ? widgetPageHref
                    : sectionBlock?.deleteUrl;
                  const linkLabel = isWidgetBlocked
                    ? t("clientConfiguration.widgetRequired.goToWidgets")
                    : t(block.titleKey);

                  return (
                    <div
                      key={opt.value}
                      className="client-config-radio-wrap client-config-radio-wrap--widget-required"
                    >
                      <div className="client-config-radio__widget-overlay">
                        <div className="client-config-radio__widget-overlay-content">
                          <span className={chipClass}>{chipContent}</span>
                          {linkHref && (
                            <a
                              href={linkHref}
                              className="client-config-radio__widget-overlay-link"
                            >
                              <span>{linkLabel}</span>
                              <SvgIcon src="/img/new-icons/arrow-right.svg" />
                            </a>
                          )}
                        </div>
                      </div>
                      {radioRow}
                    </div>
                  );
                }

                return <Fragment key={opt.value}>{radioRow}</Fragment>;
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};
